/**
 * Spec 035 v0.4, step 6: a unit's monthly token limit. Spending rolls up the tree; at the limit
 * the unit's agents stop and the head of the top unit asks people «добавить ещё?». Money is
 * people's: the limit is set here, in the app.
 */

import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { UnitBudgets } from "@/api/units";
import { Button } from "@/components/ui/Button";
import { TextField } from "@/components/ui/Field";
import { useToast } from "@/components/ui/Toast";
import { budgetLine, parseTokens, tokens } from "@/lib/units";

export function BudgetBlock({ unitId, live }: { unitId: string; live: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const budget = useQuery({ queryKey: ["departments", "budget", unitId], queryFn: () => UnitBudgets.get(unitId) });
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const b = budget.data;
  if (!b) return null;
  const parsed = text.trim() ? parseTokens(text) : null;

  const save = async (value: number | null) => {
    setSaving(true);
    try {
      await UnitBudgets.set(unitId, value, b.version || null);
      toast.success(value ? `Лимит: ${tokens(value)} токенов в месяц` : "Лимит снят");
      setEditing(false);
      void qc.invalidateQueries({ queryKey: ["departments"] });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Не сохранилось");
      void qc.invalidateQueries({ queryKey: ["departments", "budget", unitId] });
    } finally {
      setSaving(false);
    }
  };

  const lines = [...b.by_agent, ...b.by_unit].filter((line) => line.tokens > 0);
  return (
    <div className="flex flex-col gap-2 rounded-card border border-p-border bg-p-surface px-4 py-3">
      <p className={b.over ? "text-meta font-semibold text-p-blocked" : "text-meta text-p-text"}>
        {b.month}: {budgetLine(b)}
        {b.added > 0 && ` (добавлено ${tokens(b.added)})`}
      </p>
      {lines.length > 0 && (
        <ul className="flex flex-col gap-0.5 text-meta text-p-text-muted">
          {lines.map((line) => <li key={line.id}>{line.name} — {tokens(line.tokens)}</li>)}
        </ul>
      )}
      {live && !editing && (
        <Button size="compact" variant="secondary" className="self-start" onClick={() => { setText(b.tokens_per_month ? String(b.tokens_per_month) : ""); setEditing(true); }}>
          {b.tokens_per_month ? "Изменить лимит" : "Задать лимит"}
        </Button>
      )}
      {editing && (
        <div className="flex flex-col gap-2">
          <TextField label="Токенов в месяц" hint="Например: 2 млн или 500000" value={text} maxLength={20}
            error={text.trim() && !parsed ? "Число токенов, например 2 млн" : undefined}
            onChange={(e) => setText(e.target.value)} />
          <div className="flex flex-wrap gap-2">
            <Button size="compact" variant="strong" loading={saving} disabled={!parsed} onClick={() => void save(parsed)}>Сохранить</Button>
            {b.tokens_per_month !== null && (
              <Button size="compact" variant="secondary" disabled={saving} onClick={() => void save(null)}>Снять лимит</Button>
            )}
            <Button size="compact" variant="secondary" disabled={saving} onClick={() => setEditing(false)}>Отмена</Button>
          </div>
        </div>
      )}
    </div>
  );
}